/**
 * Pure types and builders for the modular UpgradeLimitDialog.
 *
 * The dialog is driven entirely by an UpgradeLimitConfig object so that future
 * limits (domains, credits, sends, etc.) can reuse the same component without
 * forking the UI. A builder describes its copy as `billing.upgradeLimit`
 * messages with their values, so a config can be built anywhere (a module
 * constant, a hook with no translator) and the dialog words it in the
 * reader's language.
 */

import type { BillingTranslator } from '@/lib/billing/billing-translator'
import {
  classifyEmailSendQuota,
  type EmailSendQuotaDetails,
  type EmailSendQuotaNumbers,
  remainingEmailSends,
} from '@/lib/billing/email-send-quota-copy'
import {
  BILLING_PLAN_ORDER,
  BILLING_PLANS,
  type BillingPlanKey,
  describePlanFeatures,
  isBillingPlanKey,
  isSunsetPlanKey,
  type SelfServePlanKey,
} from '@/lib/billing/plans'
import { defaultPriceSummary } from '@/lib/billing/price-summary'
import { GROWTH_PROMO } from '@/lib/billing/promo'
import { DEFAULT_LOCALE } from '@/lib/i18n/config'
import { dateTimeFormat, numberFormat } from '@/lib/i18n/format'
import type { Messages } from '@/lib/i18n/messages'
import { BREW_DEMO_BOOKING_URL } from '@/lib/site/booking'

export type UpgradeLimitType = 'brands' | 'domains' | 'credits' | 'emailSends'

/** Every message under `billing.upgradeLimit`, as its dotted key. */
type MessageKeyOf<Tree, Prefix extends string = ''> = {
  [Key in keyof Tree & string]: Tree[Key] extends string
    ? `${Prefix}${Key}`
    : MessageKeyOf<Tree[Key], `${Prefix}${Key}.`>
}[keyof Tree & string]

type UpgradeLimitMessageKey = MessageKeyOf<Messages['billing']['upgradeLimit']>

/**
 * A message value: as given, another `billing.upgradeLimit` message worded
 * first (a clause inside a sentence), or a calendar day (epoch ms, read in
 * UTC) worded in the reader's locale (`Oct 18`).
 */
type UpgradeLimitValue =
  | string
  | number
  | UpgradeLimitMessage
  | Readonly<{ utcDay: number }>

/** A `billing.upgradeLimit` message and the values it formats. */
type UpgradeLimitMessage = Readonly<{
  key: UpgradeLimitMessageKey
  values?: Readonly<Record<string, UpgradeLimitValue>>
}>

/**
 * Copy the upgrade dialog shows: a `billing.upgradeLimit` message (what the
 * builders below produce), several of them (sentences, in order), or text a
 * caller already put in the reader's language.
 */
export type UpgradeLimitText =
  | string
  | UpgradeLimitMessage
  | ReadonlyArray<UpgradeLimitMessage>

const UTC_DAY: Intl.DateTimeFormatOptions = {
  month: 'short',
  day: 'numeric',
  timeZone: 'UTC',
}

/**
 * {@link UpgradeLimitText} in the reader's language. `locale` words its
 * dates; the dialog passes the reader's (`useAppLocale()`).
 */
export function formatUpgradeLimitText(
  t: BillingTranslator,
  text: UpgradeLimitText,
  locale: string = DEFAULT_LOCALE
): string {
  if (typeof text === 'string') {
    return text
  }
  if (!('key' in text)) {
    return text
      .map((sentence) => formatUpgradeLimitText(t, sentence, locale))
      .join(' ')
  }
  if (!text.values) {
    return t(`upgradeLimit.${text.key}`)
  }
  const values: Record<string, string | number> = {}
  for (const [name, value] of Object.entries(text.values)) {
    if (typeof value !== 'object') {
      values[name] = value
    } else if ('utcDay' in value) {
      values[name] = dateTimeFormat(locale, UTC_DAY).format(value.utcDay)
    } else {
      values[name] = formatUpgradeLimitText(t, value, locale)
    }
  }
  return t(`upgradeLimit.${text.key}`, values)
}

export type UpgradeLimitCta = {
  label: UpgradeLimitText
  /**
   * When set, clicking the CTA triggers Stripe checkout for this plan.
   * When unset, the CTA navigates to /plan.
   */
  planKey?: SelfServePlanKey
  /**
   * When set, clicking the CTA navigates to this URL (overrides checkout).
   * Used for the Enterprise contact link.
   */
  href?: string
}

export type UpgradeLimitConfig = {
  limitType: UpgradeLimitType
  title: UpgradeLimitText
  description: UpgradeLimitText
  /**
   * Optional usage chip rendered next to the title (e.g. "3 / 1 brand
   * workspaces"). `display` replaces the `used / limit unit` reading when
   * the two numbers are not a used-of-limit pair (a shortfall).
   */
  currentUsage?: {
    used: number
    limit: number
    unit: UpgradeLimitText
    display?: UpgradeLimitText
  }
  /**
   * Plan key the dialog is recommending. Used for the highlighted CTA, and
   * the dialog lists the plan's first three features under the copy
   * ({@link describeUpgradeHighlights}).
   */
  recommendedPlanKey?: BillingPlanKey
  primaryCta: UpgradeLimitCta
  secondaryCta?: UpgradeLimitCta
}

/** How many of the recommended plan's features the dialog lists. */
const HIGHLIGHT_COUNT = 3

/**
 * The recommended plan's highlights in the reader's language: its first three
 * feature lines (none without a recommendation).
 */
export function describeUpgradeHighlights(
  t: BillingTranslator,
  locale: string,
  config: Pick<UpgradeLimitConfig, 'recommendedPlanKey'>
): ReadonlyArray<string> {
  if (!config.recommendedPlanKey) {
    return []
  }
  return describePlanFeatures(t, config.recommendedPlanKey, locale)
    .slice(0, HIGHLIGHT_COUNT)
    .map((feature) => feature.name)
}

/** The usage chip's numbers, in the reader's locale (`27,880`). */
export function formatUsageCount(value: number, locale: string): string {
  return numberFormat(locale).format(value)
}

/** A plan allowance large enough to read as "unlimited". */
function isUnlimited(count: number): boolean {
  return count >= Number.MAX_SAFE_INTEGER / 2
}

/** A count's message argument plus whether it reads as unlimited (`scope`). */
function countValues(
  name: string,
  count: number
): Record<string, string | number> {
  return {
    [name]: count,
    [`${name}Scope`]: isUnlimited(count) ? 'unlimited' : 'counted',
  }
}

export const ENTERPRISE_CONTACT_URL = BREW_DEMO_BOOKING_URL

/** The recovery link every limit dialog but the brands one offers. */
const VIEW_ALL_PLANS_CTA: UpgradeLimitCta = {
  label: { key: 'viewAllPlans' },
  href: '/plan',
}

type PlanLimitField = 'credits' | 'emailSends' | 'domains' | 'brands'

const LIMIT_TYPE_TO_PLAN_FIELD: Record<UpgradeLimitType, PlanLimitField> = {
  brands: 'brands',
  domains: 'domains',
  credits: 'credits',
  emailSends: 'emailSends',
}

/**
 * Returns the next plan tier that actually RAISES the limit the user just hit,
 * or null when no higher self-serve tier does (→ the dialog falls back to a
 * "Talk to sales" CTA).
 *
 * Limit-aware on purpose: a candidate tier only qualifies if it strictly
 * RAISES the metric the user hit. That keeps every dialog correct even if two
 * adjacent plans ever share a value on one metric (e.g. equal brand counts) —
 * the walk simply skips to the next tier that actually increases it, with no
 * per-limit special cases.
 *
 * Sunset tiers are never RECOMMENDED (they can't be bought), but they stay in
 * `BILLING_PLAN_ORDER` so an org already on one still walks forward correctly
 * from it.
 */
export function getNextPlanForLimit(
  currentPlanKey: BillingPlanKey,
  limitType: UpgradeLimitType
): BillingPlanKey | null {
  const field = LIMIT_TYPE_TO_PLAN_FIELD[limitType]
  const currentIndex = BILLING_PLAN_ORDER.indexOf(currentPlanKey)
  const currentValue = BILLING_PLANS[currentPlanKey][field]
  const startIndex = currentIndex === -1 ? 0 : currentIndex + 1

  for (let i = startIndex; i < BILLING_PLAN_ORDER.length; i++) {
    const candidateKey = BILLING_PLAN_ORDER[i]
    if (isSunsetPlanKey(candidateKey)) {
      continue
    }
    if (BILLING_PLANS[candidateKey][field] > currentValue) {
      return candidateKey
    }
  }

  return null
}

export function buildBrandLimitConfig(input: {
  currentPlanKey: BillingPlanKey
  used: number
  limit: number
}): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForLimit(input.currentPlanKey, 'brands')
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const description: UpgradeLimitMessage = recommendedPlan
    ? {
        key: 'brands.description',
        values: {
          ...countValues('limit', input.limit),
          plan: currentPlan.name,
          nextPlan: recommendedPlan.name,
          ...countValues('nextLimit', recommendedPlan.brands),
        },
      }
    : {
        key: 'brands.descriptionSales',
        values: {
          ...countValues('limit', input.limit),
          plan: currentPlan.name,
        },
      }

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'brands',
    title: { key: 'brands.title' },
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: { key: 'units.brandWorkspaces', values: { count: input.limit } },
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    primaryCta,
    // The brands dialog points at brand management (not /plan): the user can
    // delete or customize an existing brand to free a slot as an alternative
    // to upgrading. The other limit dialogs keep "View all plans" → /plan.
    secondaryCta: {
      label: { key: 'manageBrands' },
      href: '/brand',
    },
  }
}

/**
 * Build the upgrade-dialog config shown when an org has used all of its AI
 * credits for the current billing period. Mirrors `buildBrandLimitConfig`
 * so every limit dialog is data-driven from `BILLING_PLANS` — no hardcoded
 * credit numbers or plan names in UI files.
 */
export function buildCreditsLimitConfig(input: {
  currentPlanKey: BillingPlanKey
  used: number
  limit: number
}): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForLimit(
    input.currentPlanKey,
    'credits'
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  // "no AI credits" below one: a balance never reads negative.
  const description: UpgradeLimitMessage = recommendedPlan
    ? {
        key: 'credits.description',
        values: {
          limit: Math.max(0, input.limit),
          plan: currentPlan.name,
          nextPlan: recommendedPlan.name,
          nextLimit: Math.max(0, recommendedPlan.credits),
        },
      }
    : {
        key: 'credits.descriptionSales',
        values: { limit: Math.max(0, input.limit), plan: currentPlan.name },
      }

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'credits',
    title: { key: 'credits.title' },
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: { key: 'units.aiCredits', values: { count: input.limit } },
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    primaryCta,
    secondaryCta: VIEW_ALL_PLANS_CTA,
  }
}

/**
 * Build the upgrade-dialog config shown when a contact-validation run costs
 * more credits than the org has left. Unlike `buildCreditsLimitConfig` (a
 * fully-drained balance), this is a SHORTFALL dialog: the usage chip shows
 * `remaining / required credits` so the user sees exactly how far short they
 * are of the quoted validation cost.
 */
export function buildValidationCreditsConfig(input: {
  currentPlanKey: BillingPlanKey
  requiredCredits: number
  remainingCredits: number
}): UpgradeLimitConfig {
  const recommendedPlanKey = getNextPlanForLimit(
    input.currentPlanKey,
    'credits'
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const required = Math.max(0, Math.round(input.requiredCredits))
  const remaining = Math.max(0, Math.floor(input.remainingCredits))

  const description: UpgradeLimitMessage = recommendedPlan
    ? {
        key: 'validation.description',
        values: {
          required,
          remaining,
          nextPlan: recommendedPlan.name,
          nextLimit: Math.max(0, recommendedPlan.credits),
        },
      }
    : { key: 'validation.descriptionSales', values: { required, remaining } }

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'credits',
    title: { key: 'validation.title' },
    description,
    currentUsage: {
      used: remaining,
      limit: required,
      unit: { key: 'units.credits', values: { count: required } },
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    primaryCta,
    secondaryCta: VIEW_ALL_PLANS_CTA,
  }
}

/**
 * Build the upgrade-dialog config for the sending-domain limit. Sending
 * domains are UNLIMITED on every plan (2026-08-19), so the add-domain gate in
 * `domain-settings.tsx` can no longer fire and this config is a defensive
 * fallback — it must still read sanely if a future plan reintroduces a cap.
 */
export function buildDomainsLimitConfig(input: {
  currentPlanKey: BillingPlanKey
  used: number
  limit: number
}): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForLimit(
    input.currentPlanKey,
    'domains'
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null

  const buildFallbackDescription = (): UpgradeLimitMessage => {
    if (input.limit >= Number.MAX_SAFE_INTEGER) {
      return { key: 'domains.unlimited' }
    }
    return {
      key: 'domains.descriptionSales',
      values: { ...countValues('limit', input.limit), plan: currentPlan.name },
    }
  }
  const description: UpgradeLimitMessage = recommendedPlan
    ? {
        key: 'domains.description',
        values: {
          ...countValues('limit', input.limit),
          plan: currentPlan.name,
          nextPlan: recommendedPlan.name,
          ...countValues('nextLimit', recommendedPlan.domains),
        },
      }
    : buildFallbackDescription()

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  return {
    limitType: 'domains',
    title: { key: 'domains.title' },
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: { key: 'units.sendingDomains', values: { count: input.limit } },
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    primaryCta,
    secondaryCta: VIEW_ALL_PLANS_CTA,
  }
}

/**
 * The next tier whose monthly sends exceed the org's CURRENT period limit,
 * not just its plan's catalog value: a prepaid term (6 × Growth = 300,000)
 * must never be "upgraded" to Pro's 200,000.
 */
function getNextPlanForSends(
  currentPlanKey: BillingPlanKey,
  periodLimit: number
): BillingPlanKey | null {
  let candidate = getNextPlanForLimit(currentPlanKey, 'emailSends')
  while (candidate && BILLING_PLANS[candidate].emailSends <= periodLimit) {
    candidate = getNextPlanForLimit(candidate, 'emailSends')
  }
  return candidate
}

/** What the email-send dialog renders: the plan, its numbers, the refusal. */
export type EmailSendsLimitInput = {
  currentPlanKey: BillingPlanKey
} & Pick<EmailSendQuotaDetails, 'used' | 'limit'> &
  Partial<Pick<EmailSendQuotaDetails, 'reserved' | 'requested' | 'periodEnd'>>

/**
 * Build the email-send upgrade dialog. Admission is `limit − used −
 * reserved`, so a refused send has three honest stories (see
 * `classifyEmailSendQuota`): the allowance is used up, this send is bigger
 * than what is left, or sends in progress reserved the rest. Only the first
 * says "used all". The sentences say what the shared `describeEmailSendQuota`
 * says, in the reader's language; plan numbers come straight from
 * `BILLING_PLANS`.
 */
export function buildEmailSendsLimitConfig(
  input: EmailSendsLimitInput
): UpgradeLimitConfig {
  const currentPlan = BILLING_PLANS[input.currentPlanKey]
  const recommendedPlanKey = getNextPlanForSends(
    input.currentPlanKey,
    input.limit
  )
  const recommendedPlan = recommendedPlanKey
    ? BILLING_PLANS[recommendedPlanKey]
    : null
  const quota = {
    used: input.used,
    reserved: input.reserved ?? 0,
    limit: input.limit,
    requested: input.requested,
  }
  const remaining = remainingEmailSends(quota)
  const kind = classifyEmailSendQuota(quota)
  // A zero (or unknown) allowance reads as "unlimited email sends".
  const upgradeSentence: UpgradeLimitMessage = recommendedPlan
    ? {
        key: 'emailSends.upgrade',
        values: {
          nextPlan: recommendedPlan.name,
          nextLimit: Math.max(0, recommendedPlan.emailSends),
        },
      }
    : { key: 'emailSends.sales' }
  const description: Array<UpgradeLimitMessage> = [
    emailSendsQuotaSentence(quota, currentPlan.name),
    upgradeSentence,
  ]
  if (kind === 'shortfall') {
    description.push({ key: 'emailSends.fewerRecipients' })
  }
  if (input.periodEnd !== undefined) {
    description.push({
      key: 'emailSends.resets',
      values: { date: { utcDay: input.periodEnd } },
    })
  }

  const primaryCta = buildPrimaryCta(recommendedPlanKey, recommendedPlan?.name)

  if (kind === 'shortfall' || kind === 'reserved_only') {
    const isShortfall = kind === 'shortfall'
    const requested = quota.requested ?? 0
    return {
      limitType: 'emailSends',
      title: isShortfall
        ? { key: 'emailSends.titleShortfall' }
        : { key: 'emailSends.titleSendingNow' },
      description,
      currentUsage: isShortfall
        ? {
            used: remaining,
            limit: requested,
            unit: { key: 'units.emailSends', values: { count: requested } },
            display: {
              key: 'emailSends.chipShortfall',
              values: {
                remaining: wholeSends(remaining),
                requested: wholeSends(requested),
              },
            },
          }
        : {
            used: quota.used + quota.reserved,
            limit: quota.limit,
            unit: { key: 'units.emailSends', values: { count: quota.limit } },
            display: {
              key: 'emailSends.chipSendingNow',
              values: {
                reserved: wholeSends(quota.reserved),
                used: wholeSends(quota.used),
                limit: wholeSends(quota.limit),
              },
            },
          },
      recommendedPlanKey: recommendedPlanKey ?? undefined,
      primaryCta,
      secondaryCta: VIEW_ALL_PLANS_CTA,
    }
  }

  return {
    limitType: 'emailSends',
    title: { key: 'emailSends.title' },
    description,
    currentUsage: {
      used: input.used,
      limit: input.limit,
      unit: { key: 'units.emailSends', values: { count: input.limit } },
    },
    recommendedPlanKey: recommendedPlanKey ?? undefined,
    primaryCta,
    secondaryCta: VIEW_ALL_PLANS_CTA,
  }
}

/** A send count as the copy states it: whole, never below zero. */
function wholeSends(count: number): number {
  return Math.max(0, Math.floor(count))
}

/**
 * `describeEmailSendQuota` (the sentence the API, MCP and notifications
 * share) as a `billing.upgradeLimit` message, so the dialog words it in the
 * reader's language: where the allowance went, and why this send does not fit.
 */
function emailSendsQuotaSentence(
  quota: EmailSendQuotaNumbers,
  plan: string
): UpgradeLimitMessage {
  const remaining = wholeSends(remainingEmailSends(quota))
  const usage: UpgradeLimitMessage =
    quota.reserved > 0
      ? {
          key: 'emailSends.usageSending',
          values: {
            used: wholeSends(quota.used),
            reserved: wholeSends(quota.reserved),
          },
        }
      : {
          key: 'emailSends.usageUsed',
          values: {
            used: wholeSends(quota.used),
            limit: wholeSends(quota.limit),
          },
        }
  switch (classifyEmailSendQuota(quota)) {
    case 'exhausted':
      return {
        key: 'emailSends.exhausted',
        values: { limit: wholeSends(quota.limit), plan },
      }
    case 'shortfall':
      return {
        key: 'emailSends.shortfall',
        values: {
          requested: wholeSends(quota.requested ?? 0),
          remaining,
          plan,
          usage,
        },
      }
    case 'reserved_only':
      return { key: 'emailSends.sendingNow', values: { plan, usage } }
    case 'available':
      return {
        key: 'emailSends.available',
        values: { remaining, limit: wholeSends(quota.limit), plan, usage },
      }
  }
}

/**
 * A refused send in the reader's language, for a surface that cannot open the
 * upgrade dialog and says it in a toast: the dialog's sentences (what the
 * allowance went to, the upgrade, the reset day). The server's `error` says
 * the same in English (`describeEmailSendQuota` and its remedies); `null`
 * for a plan Brew does not know, where that English stands.
 */
export function describeEmailSendRefusal(
  t: BillingTranslator,
  quota: EmailSendQuotaDetails,
  locale: string
): string | null {
  if (!isBillingPlanKey(quota.planKey)) {
    return null
  }
  const config = buildEmailSendsLimitConfig({
    currentPlanKey: quota.planKey,
    used: quota.used,
    limit: quota.limit,
    reserved: quota.reserved,
    requested: quota.requested,
    periodEnd: quota.periodEnd,
  })
  return formatUpgradeLimitText(t, config.description, locale)
}

export function showEmailSendsUpgrade(
  showUpgrade: (config: UpgradeLimitConfig) => void,
  input: EmailSendsLimitInput
): void {
  showUpgrade(buildEmailSendsLimitConfig(input))
}

function buildPrimaryCta(
  recommendedPlanKey: BillingPlanKey | null,
  recommendedPlanName: string | undefined
): UpgradeLimitCta {
  if (!recommendedPlanKey) {
    return {
      label: { key: 'talkToSales' },
      href: ENTERPRISE_CONTACT_URL,
    }
  }

  if (recommendedPlanKey === 'enterprise') {
    return {
      label: { key: 'talkToSales' },
      href: ENTERPRISE_CONTACT_URL,
    }
  }

  return {
    label: recommendedPlanName
      ? { key: 'upgradeTo', values: { plan: recommendedPlanName } }
      : { key: 'upgradeToNextPlan' },
    planKey: recommendedPlanKey as SelfServePlanKey,
  }
}

/**
 * At a usage limit the primary CTA stays month-to-month — that unblocks the
 * action with the lowest up-front charge. Eligible never-paid orgs still need
 * a path to the prepaid offer, which is the Starter replacement. Rewrite only
 * the "View all plans" recovery link; brand-management and sales CTAs stay.
 * The discount is a percent in the reader's `locale` (`50%`, `50 %`).
 */
export function withPromoLimitSecondaryCta(
  config: UpgradeLimitConfig,
  isEligible: boolean,
  locale: string
): UpgradeLimitConfig {
  if (!isEligible) {
    return config
  }
  if (config.recommendedPlanKey !== GROWTH_PROMO.planKey) {
    return config
  }
  if (config.secondaryCta?.href !== '/plan') {
    return config
  }

  const price = defaultPriceSummary()[GROWTH_PROMO.planKey]
  if (!price) {
    return config
  }

  return {
    ...config,
    secondaryCta: {
      href: '/plan',
      // Keep the navigational meaning ("this compares plans") — a user at a
      // hard limit still needs a labelled way to weigh options, not only an
      // offer string.
      label: {
        key: 'viewAllPlansWithPromo',
        values: {
          percent: numberFormat(locale, { style: 'percent' }).format(
            GROWTH_PROMO.percentOff / 100
          ),
          months: GROWTH_PROMO.termMonths,
        },
      },
    },
  }
}
